import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Plus, Loader2, Check, AlertCircle, ExternalLink, Upload, Image, X, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { copiesAPI, discogsAPI, releasesAPI, uploadsAPI, wishlistAPI } from '../lib/api';
import { parseDiscogsTitle, CONDITION_OPTIONS, formatCurrency } from '../lib/utils';

export default function SearchResults({ results, onSelect, onBack, aiSuggestion = null }) {
  const navigate = useNavigate();
  const [selectedResult, setSelectedResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [conditionVinyl, setConditionVinyl] = useState('VG+');
  const [conditionCover, setConditionCover] = useState('VG+');
  const [existingInfo, setExistingInfo] = useState(null);
  const [showExistingDialog, setShowExistingDialog] = useState(false);
  const [marketStats, setMarketStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [customCoverImage, setCustomCoverImage] = useState(null);
  const [customCoverPreview, setCustomCoverPreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [addingToWishlist, setAddingToWishlist] = useState(false);
  const fileInputRef = useRef(null);

  const handleCustomImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/)) {
        toast.error('Upload venligst et JPEG, PNG, eller WebP billede');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be max 5MB');
        return;
      }
      setCustomCoverImage(file);
      setCustomCoverPreview(URL.createObjectURL(file));
    }
  };

  const clearCustomImage = () => {
    setCustomCoverImage(null);
    setCustomCoverPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleResultClick = async (result) => {
    if (selectedResult?.id === result.id) {
      setSelectedResult(null);
      setExistingInfo(null);
      setMarketStats(null);
      clearCustomImage();
      return;
    }
    
    setSelectedResult(result);
    setExistingInfo(null);
    setMarketStats(null);
    
    // Determine the correct release ID to check
    // For master results, we need to get the main_release first
    let releaseIdToCheck = result.id;
    
    if (result.type === 'master') {
      try {
        console.log(`[ResultClick] Fetching master ${result.id} to determine main release`);
        const masterRes = await discogsAPI.getMaster(result.id);
        if (masterRes.data?.main_release) {
          releaseIdToCheck = masterRes.data.main_release;
          console.log(`[ResultClick] Using main_release ${releaseIdToCheck} for checks`);
        }
      } catch (err) {
        console.warn('[ResultClick] Could not fetch master info');
      }
    }
    
    // Check if user already has copies of this release
    try {
      const existingCheck = await releasesAPI.checkExisting(releaseIdToCheck);
      if (existingCheck.data.has_copies) {
        setExistingInfo(existingCheck.data);
      }
    } catch (err) {
      // Ignore check errors
    }
    
    // Fetch marketplace stats using the release ID
    setLoadingStats(true);
    try {
      const releaseRes = await discogsAPI.getRelease(releaseIdToCheck);
      setMarketStats(releaseRes.data.marketplace_stats);
    } catch (err) {
      // Stats not available
    } finally {
      setLoadingStats(false);
    }
  };

  const handleAddToCollection = async (forceAdd = false) => {
    if (!selectedResult) return;
    
    // If has existing copies and not forcing, show dialog
    if (existingInfo && !forceAdd) {
      setShowExistingDialog(true);
      return;
    }
    
    setLoading(true);
    
    try {
      // CRITICAL FIX: Handle master vs release types correctly
      // When result type is "master", the id is a master_id, NOT a release_id
      // We need to get the main release from the master first
      let releaseId = selectedResult.id;
      let releaseData = null;
      let masterId = selectedResult.master_id;

      console.log(`[AddToCollection] Selected result type: ${selectedResult.type}, id: ${selectedResult.id}`);

      if (selectedResult.type === 'master') {
        // For master results, fetch the master to get the main_release
        try {
          console.log(`[AddToCollection] Fetching master ${selectedResult.id} to get main release`);
          const masterRes = await discogsAPI.getMaster(selectedResult.id);
          const mainReleaseId = masterRes.data?.main_release;
          
          if (mainReleaseId) {
            console.log(`[AddToCollection] Master's main_release is ${mainReleaseId}`);
            releaseId = mainReleaseId;
            masterId = selectedResult.id; // The original ID was the master_id
          } else {
            console.warn('[AddToCollection] Master has no main_release, using master ID as fallback');
          }
        } catch (err) {
          console.error('[AddToCollection] Failed to fetch master:', err);
        }
      }

      // Now fetch the actual release details
      try {
        console.log(`[AddToCollection] Fetching release ${releaseId}`);
        const res = await discogsAPI.getRelease(releaseId);
        releaseData = res.data.release;
        console.log(`[AddToCollection] Got release data: ${releaseData?.artists?.[0]?.name} - ${releaseData?.title}`);
      } catch (err) {
        console.warn('[AddToCollection] Could not fetch release details:', err);
      }

      const { artist, title } = parseDiscogsTitle(selectedResult.title);
      
      // Handle custom cover image upload
      let coverImageUrl = releaseData?.images?.[0]?.uri || selectedResult.cover_image;
      
      if (customCoverImage) {
        try {
          setUploadingImage(true);
          const uploadRes = await uploadsAPI.uploadCover(customCoverImage);
          coverImageUrl = uploadRes.data.url;
          console.log(`[AddToCollection] Uploaded custom cover: ${coverImageUrl}`);
        } catch (err) {
          console.error('[AddToCollection] Failed to upload custom cover:', err);
          toast.error('Could not upload image, using default');
        } finally {
          setUploadingImage(false);
        }
      }
      
      // Prepare release data for backend - use the CORRECT release ID
      // Use median price if available, otherwise fallback to lowest_price as estimate
      const estimatedValue = marketStats?.median?.value || marketStats?.lowest_price?.value || null;
      const valueCurrency = marketStats?.median?.currency || marketStats?.lowest_price?.currency || null;
      
      const releasePayload = {
        discogs_release_id: releaseId,
        discogs_master_id: masterId,
        artist: releaseData?.artists?.[0]?.name || artist || 'Unknown Artist',
        title: releaseData?.title || title || selectedResult.title,
        year: releaseData?.year || selectedResult.year,
        genres: releaseData?.genres || selectedResult.genre || [],
        styles: releaseData?.styles || selectedResult.style || [],
        cover_image_url: coverImageUrl,
        market_value_median: estimatedValue,
        market_value_low: marketStats?.lowest_price?.value || null,
        market_value_high: marketStats?.highest_price?.value || null,
        market_value_currency: valueCurrency,
        label: releaseData?.labels?.[0]?.name || selectedResult.label?.[0],
        catno: releaseData?.labels?.[0]?.catno || selectedResult.catno,
        format: releaseData?.formats?.[0]?.name || selectedResult.format?.[0],
        country: releaseData?.country || selectedResult.country,
      };

      console.log(`[AddToCollection] Sending to backend: release_id=${releaseId}, artist=${releasePayload.artist}, title=${releasePayload.title}`);

      // Add copy with release data
      await copiesAPI.add({
        discogs_release_id: releaseId,
        condition_vinyl: conditionVinyl,
        condition_cover: conditionCover,
      }, releasePayload);
      
      clearCustomImage();
      setShowExistingDialog(false);
      onSelect();
    } catch (err) {
      console.error('[AddToCollection] Error:', err);
      if (err.response?.status === 403) {
        toast.error(err.response?.data?.detail || 'Limit reached. Upgrade to PRO.');
      } else {
        toast.error('Failed to add album');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddToWishlist = async () => {
    if (!selectedResult) return;
    
    setAddingToWishlist(true);
    try {
      // Get the correct release ID (handle master vs release)
      let releaseId = selectedResult.id;
      let releaseData = null;
      
      if (selectedResult.type === 'master') {
        const masterRes = await discogsAPI.getMaster(selectedResult.id);
        releaseId = masterRes.data.master?.main_release || selectedResult.id;
        releaseData = masterRes.data.master;
      }
      
      // Get full release details
      const fullReleaseRes = await discogsAPI.getRelease(releaseId);
      const fullRelease = fullReleaseRes.data.release;
      const stats = fullReleaseRes.data.marketplace_stats;
      
      const { artist, title } = parseDiscogsTitle(selectedResult.title);
      
      const wishlistItem = {
        discogs_release_id: releaseId,
        discogs_master_id: releaseData?.id || selectedResult.master_id,
        artist: fullRelease?.artists?.[0]?.name || artist,
        title: fullRelease?.title || title,
        year: fullRelease?.year || selectedResult.year,
        label: fullRelease?.labels?.[0]?.name || selectedResult.label?.[0],
        catno: fullRelease?.labels?.[0]?.catno || selectedResult.catno,
        country: fullRelease?.country || selectedResult.country,
        format: fullRelease?.formats?.[0]?.name || selectedResult.format?.[0],
        cover_image_url: fullRelease?.images?.[0]?.uri || selectedResult.cover_image,
        genres: fullRelease?.genres || selectedResult.genre || [],
        styles: fullRelease?.styles || selectedResult.style || [],
        market_value_median: stats?.median?.value || stats?.lowest_price?.value,
        market_value_low: stats?.lowest_price?.value,
        market_value_high: stats?.highest_price?.value,
        market_value_currency: stats?.median?.currency || stats?.lowest_price?.currency,
      };
      
      const response = await wishlistAPI.add(wishlistItem);
      
      if (response.data.warning) {
        toast.warning(response.data.warning);
      }
      toast.success('Added to wishlist');
      
      // Clear selection
      setSelectedResult(null);
      setMarketStats(null);
      
    } catch (err) {
      if (err.response?.data?.detail) {
        toast.error(err.response.data.detail);
      } else {
        toast.error('Could not add to wishlist');
      }
    } finally {
      setAddingToWishlist(false);
    }
  };

  const placeholderImage = 'https://images.unsplash.com/photo-1642444525640-d3eb287ed389?w=100&h=100&fit=crop';

  return (
    <div className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="gap-2"
        data-testid="back-to-search-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Search
      </Button>

      {/* AI Suggestion Banner */}
      {aiSuggestion && (
        <Alert className="bg-primary/10 border-primary/20">
          <AlertCircle className="h-4 w-4 text-primary" />
          <AlertDescription className="text-sm">
            <span className="font-medium">AI suggests – you confirm:</span>
            {aiSuggestion.artist && <span> Artist: {aiSuggestion.artist}</span>}
            {aiSuggestion.artist && aiSuggestion.title && <span> •</span>}
            {aiSuggestion.title && <span> Album: {aiSuggestion.title}</span>}
            <span className="text-muted-foreground ml-2">({aiSuggestion.confidence} confidence)</span>
          </AlertDescription>
        </Alert>
      )}

      <p className="text-sm text-muted-foreground">
        {results.length} result{results.length !== 1 ? 's' : ''} found. Select an album to add.
      </p>

      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {results.map((result) => {
          const isSelected = selectedResult?.id === result.id;
          const { artist, title } = parseDiscogsTitle(result.title);
          
          return (
            <div
              key={result.id}
              onClick={() => handleResultClick(result)}
              className={`search-result-item ${isSelected ? 'border-primary bg-secondary' : ''}`}
              data-testid={`search-result-${result.id}`}
            >
              <div className="w-16 h-16 flex-shrink-0 rounded-sm overflow-hidden bg-secondary">
                <img
                  src={result.thumb || result.cover_image || placeholderImage}
                  alt={result.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-sm truncate">{title || result.title}</h4>
                <p className="text-sm text-muted-foreground truncate">{artist}</p>
                <div className="flex flex-wrap gap-x-2 gap-y-1 mt-1 text-xs text-muted-foreground">
                  {result.year && <span>{result.year}</span>}
                  {result.label?.[0] && <span>• {result.label[0]}</span>}
                  {result.format?.[0] && <span>• {result.format[0]}</span>}
                  {result.country && <span>• {result.country}</span>}
                </div>
              </div>
              {isSelected && (
                <Check className="w-5 h-5 text-primary flex-shrink-0" />
              )}
            </div>
          );
        })}
      </div>

      {selectedResult && (
        <div className="space-y-4 pt-4 border-t border-border">
          {/* Existing copies info */}
          {existingInfo && (
            <Alert className="bg-primary/10 border-primary/20">
              <AlertCircle className="h-4 w-4 text-primary" />
              <AlertDescription className="flex items-center justify-between">
                <span>
                  You have allerede {existingInfo.copy_count} kopi{existingInfo.copy_count > 1 ? 'er' : ''} af denne udgivelse
                </span>
                <Button
                  variant="link"
                  size="sm"
                  className="text-primary p-0 h-auto"
                  onClick={() => navigate(`/release/${selectedResult.id}`)}
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Se eksisterende
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Market Stats */}
          <div className="p-3 bg-secondary/50 rounded-sm">
            <h5 className="text-sm font-medium mb-2">Market Value</h5>
            {loadingStats ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading stats...</span>
              </div>
            ) : marketStats?.available && marketStats?.median ? (
              <div className="space-y-1">
                <p className="text-lg font-bold text-primary">
                  {formatCurrency(marketStats.median.value, marketStats.median.currency)}
                  <span className="text-sm font-normal text-muted-foreground ml-2">median</span>
                </p>
                {(marketStats.lowest_price || marketStats.highest_price) && (
                  <p className="text-xs text-muted-foreground">
                    Range: {marketStats.lowest_price ? formatCurrency(marketStats.lowest_price.value, marketStats.lowest_price.currency) : '-'} 
                    {' – '}
                    {marketStats.highest_price ? formatCurrency(marketStats.highest_price.value, marketStats.highest_price.currency) : '-'}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  {marketStats.num_for_sale} for sale on Discogs
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No marketplace stats available</p>
            )}
          </div>

          <h4 className="font-medium">Set Condition</h4>
          
          {/* Custom Cover Image Upload */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleCustomImageChange}
            accept="image/jpeg,image/jpg,image/png,image/webp"
            className="hidden"
          />
          
          {(!selectedResult.cover_image || selectedResult.cover_image.includes('spacer.gif')) && !customCoverPreview && (
            <Alert className="bg-yellow-500/10 border-yellow-500/20">
              <Image className="h-4 w-4 text-yellow-500" />
              <AlertDescription className="flex items-center justify-between">
                <span className="text-sm">Intet coverbillede fra Discogs</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-1"
                  data-testid="upload-cover-btn"
                >
                  <Upload className="w-3 h-3" />
                  Upload
                </Button>
              </AlertDescription>
            </Alert>
          )}
          
          {customCoverPreview && (
            <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-sm">
              <img
                src={customCoverPreview}
                alt="Custom cover"
                className="w-16 h-16 rounded-sm object-cover"
              />
              <div className="flex-1">
                <p className="text-sm font-medium">Eget coverbillede</p>
                <p className="text-xs text-muted-foreground">Vil blive brugt i stedet for Discogs billede</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearCustomImage}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
          
          {selectedResult.cover_image && !selectedResult.cover_image.includes('spacer.gif') && !customCoverPreview && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="w-full gap-2 text-muted-foreground"
            >
              <Upload className="w-4 h-4" />
              Upload eget coverbillede i stedet
            </Button>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Vinyl</label>
              <Select value={conditionVinyl} onValueChange={setConditionVinyl}>
                <SelectTrigger className="bg-secondary border-border" data-testid="result-vinyl-condition">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONDITION_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Cover</label>
              <Select value={conditionCover} onValueChange={setConditionCover}>
                <SelectTrigger className="bg-secondary border-border" data-testid="result-cover-condition">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONDITION_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => handleAddToCollection()}
              disabled={loading || addingToWishlist}
              className="flex-1 gap-2 bg-primary text-primary-foreground"
              data-testid="add-to-collection-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  {existingInfo ? 'Add kopi' : 'Add to Collection'}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleAddToWishlist}
              disabled={loading || addingToWishlist}
              className="gap-2"
              data-testid="add-to-wishlist-btn"
            >
              {addingToWishlist ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Heart className="w-4 h-4 text-pink-500" />
              )}
              <span className="hidden sm:inline">Wishlist</span>
            </Button>
          </div>
        </div>
      )}

      {/* Existing copies dialog */}
      <Dialog open={showExistingDialog} onOpenChange={setShowExistingDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>You have allerede denne udgivelse</DialogTitle>
            <DialogDescription>
              You have {existingInfo?.copy_count} cop${existingInfo?.copy_count > 1 ? 'ies' : 'y'} of this release. Do you want to add another copy?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowExistingDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                setShowExistingDialog(false);
                navigate(`/release/${selectedResult?.id}`);
              }}
            >
              Go to existing
            </Button>
            <Button 
              onClick={() => handleAddToCollection(true)}
              disabled={loading}
              className="bg-primary text-primary-foreground"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add kopi'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
